"use client";

import styles from "@/styles/modules/components/header/bookMenuOptions/bookMenuOptions.module.scss";

import { BookMenuContext } from "@/context/bookMenuContext";
import { LanguageContext } from "@/context/languageContext";

import { translate } from "@/utils/translate";
import { submitFormMeeting } from "@/wordpress/queries/formSubmit";
import { usePathname } from "next/navigation";
import { FormEvent, useCallback, useContext, useEffect, useRef, useState } from "react";
import TurnstileWidget from "@/components/TurnstileWidget";

export default function BookMenuMeetingsEvents() {
  const pathname = usePathname();

  const { language } = useContext(LanguageContext);

  const dateRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const surnameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const guestsRef = useRef<HTMLSelectElement>(null);
  const roomsRef = useRef<HTMLSelectElement>(null);
  const companyRef = useRef<HTMLInputElement>(null);


  const [nameValue, setNameValue] = useState<string>("");
  const [lastnameValue, setLastnameValue] = useState<string>("");
  const [companyValue, setCompanyValue] = useState<string>("");
  const [emailValue, setEmailValue] = useState<string>("");
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [dateValue, setDateValue] = useState<string>("");
  const [guestsValue, setGuestsValue] = useState<string>("1");
  const [roomsValue, setRoomsValue] = useState<string>("1");

  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | ''>('');
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, []);

  const { closeBookMenu } = useContext(BookMenuContext); // Access closeBookMenu from BookMenuContext

  const handleMenuClose = () => {
    // Clear form fields
    setNameValue("");
    setLastnameValue("");
    setCompanyValue("");
    setEmailValue("");
    setPhoneValue("");
    setDateValue("");
    setGuestsValue("1");
    setRoomsValue("1");
    // Reset submitStatus
    setSubmitStatus('');
  };

  useEffect(() => {
    setTimeout(() => {
      handleMenuClose();
    }, 2000)
  }, [submitStatus]);


  useEffect(() => {
    setNameValue("");
    setLastnameValue("");
    setCompanyValue("");
    setEmailValue("");
    setPhoneValue("");
    setDateValue("");
    setGuestsValue("1");
    setRoomsValue("1");
  }, [pathname]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      setSubmitStatus('error');
      return;
    }

    setIsVerifying(true);

    try {
      const verifyRes = await fetch("/api/verify-turnstile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: turnstileToken }),
      });

      const verifyData = await verifyRes.json();

      if (!verifyData.success) {
        setSubmitStatus('error');
        setIsVerifying(false);
        return;
      }
    } catch {
      setSubmitStatus('error');
      setIsVerifying(false);
      return;
    }

    try {
      const formData = {
        company: companyRef.current?.value,
        name: nameRef.current?.value,
        surname: surnameRef.current?.value,
        email: emailRef.current?.value,
        phone: phoneRef.current?.value,
        date: dateRef.current?.value || "",
        guests: guestsRef.current?.value,
        rooms: roomsRef.current?.value
      };

      // console.log('BookMenuMeetingsEvents: Form data being submitted:', formData);

      const response = await submitFormMeeting(formData);
      // console.log('BookMenuMeetingsEvents: Response from submitForm:', response);

      if (!response) {
        setSubmitStatus('error');
        console.error('BookMenuMeetingsEvents: No response from submitForm');
      } else {
        setSubmitStatus('success');
        setTurnstileToken("");
        setTimeout(() => {
          closeBookMenu(); // Call closeBookMenu from BookMenuContext on successful form submission
        }, 2000);
      }

    } catch (error) {
      setSubmitStatus('error');
      console.error('BookMenuMeetingsEvents: Error submitting form:', error);
    }

    setIsVerifying(false);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.formRow}>
        <div className={`${styles.formField} ${styles.half}`}>
          <label htmlFor="name">{translate["name"][language]}*</label>
          <input
            type="text"
            name="name"
            id="name"
            placeholder={translate["firstName"][language]}
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            autoComplete="on"
            ref={nameRef}
            required
          />
        </div>
        <div className={`${styles.formField} ${styles.half}`}>
          <input
            type="text"
            name="lastname"
            id="lastname"
            placeholder={translate["lastName"][language]}
            value={lastnameValue}
            onChange={(e) => setLastnameValue(e.target.value)}
            autoComplete="on"
            ref={surnameRef}
          />
        </div>
      </div>
      <div className={styles.formField}>
        <label htmlFor="company-name">
          {translate["companyName"][language]}
        </label>
        <input
          type="text"
          name="company-name"
          id="company-name"
          placeholder=""
          autoComplete="on"
          value={companyValue}
          onChange={(e) => setCompanyValue(e.target.value)}
          ref={companyRef}
        />
      </div>
      <div className={styles.formField}>
        <label htmlFor="email">{translate["email"][language]}*</label>
        <input
          type="email"
          name="email"
          id="email"
          placeholder=""
          value={emailValue}
          onChange={(e) => setEmailValue(e.target.value)}
          autoComplete="on"
          ref={emailRef}
          required
        />
      </div>
      <div className={styles.formField}>
        <label htmlFor="phone">{translate["phone"][language]}</label>
        <input
          type="tel"
          name="phone"
          id="phone"
          placeholder=""
          autoComplete="on"
          value={phoneValue}
          onChange={(e) => setPhoneValue(e.target.value)}
          ref={phoneRef}
        />
      </div>
      <div className={styles.formField}>
        <label htmlFor="date">{translate["date"][language]}</label>
        <input
          type="date"
          name="date"
          id="date"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          autoComplete="on"
          ref={dateRef}
        />
      </div>
      <div className={styles.formRow}>
        <div className={`${styles.formField} ${styles.half}`}>
          <label htmlFor="guests">{translate["guests"][language]}</label>
          <select
            name="guests"
            id="guests"
            value={guestsValue}
            onChange={(e) => setGuestsValue(e.target.value)}
            ref={guestsRef}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4_9">4-9</option>
            <option value="10_25">10-25</option>
          </select>
        </div>
        <div className={`${styles.formField} ${styles.half}`}>
          <label htmlFor="rooms">{translate["rooms"][language]}</label>
          <select
            name="rooms"
            id="rooms"
            value={roomsValue}
            onChange={(e) => setRoomsValue(e.target.value)}
            ref={roomsRef}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
        </div>
      </div>
      {submitStatus === 'success' && (
        <div className={styles.feedbackLabel}>{translate["formBookingSuccess"][language]}</div>
      )}
      {submitStatus === 'error' && (
        <div className={styles.feedbackLabel}>{translate["formBookingError"][language]}</div>
      )}
      <div className={`${styles.formField} ${styles.turnstileWrapper}`}>
        <TurnstileWidget
          siteKey={process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY!}
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
        />
      </div>
      <div className={styles.formField}>
        <input
          type="submit"
          name="submit"
          value={translate["makeAnInquiry"][language]}
          disabled={isVerifying}
        />
      </div>
    </form>
  );
}
